/**
 * UpdateChecker Component
 * Gemäß Spezifikation: AutoUpdate_System_EasyWork.pdf
 * 
 * Features:
 * - Update-Badge in der Navbar (grün pulsierend)
 * - Update-Modal mit Versionsvergleich
 * - Live-Terminal für Deploy-Logs (SSE)
 * - Erfolgs-/Fehlermeldungen
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Download, X, RefreshCw, CheckCircle, XCircle, Copy, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import apiFetch from '../../utils/apiFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 Minuten

export function UpdateChecker() {
  const { user, isAuthenticated } = useAuth();
  const [updateInfo, setUpdateInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployLogs, setDeployLogs] = useState([]);
  const [deployStatus, setDeployStatus] = useState(null); // 'success' | 'failed' | null
  const [countdown, setCountdown] = useState(null);
  const terminalRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Check for updates
  const checkForUpdates = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await apiFetch('/admin/update-check', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUpdateInfo(data);
      }
    } catch (error) {
      // Silently ignore errors (network issues, etc.)
      // Don't log to console to avoid DataCloneError with some extensions
    }
  }, [isAuthenticated, isAdmin]);

  // Initial check and interval
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      checkForUpdates();
      intervalRef.current = setInterval(checkForUpdates, CHECK_INTERVAL);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, isAdmin, checkForUpdates]);

  // Start deployment
  const startDeploy = async () => {
    setIsDeploying(true);
    setDeployLogs([]);
    setDeployStatus(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch('/admin/update-deploy', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }, { timeoutMs: 0 });
      
      if (!response.ok) {
        if (response.status === 409) {
          toast.error('Ein Deployment läuft bereits. Bitte warte.');
          setIsDeploying(false);
          return;
        }
        throw new Error('Deploy request failed');
      }
      
      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const text = decoder.decode(value);
        const lines = text.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const message = line.substring(6);
            
            if (message === 'UPDATE_SUCCESS') {
              setDeployStatus('success');
              setIsDeploying(false);
              startCountdown();
            } else if (message === 'UPDATE_FAILED') {
              setDeployStatus('failed');
              setIsDeploying(false);
            } else if (message.trim()) {
              setDeployLogs(prev => [...prev, message]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Deploy error:', error);
      setDeployLogs(prev => [...prev, `FEHLER: ${error.message}`]);
      setDeployStatus('failed');
      setIsDeploying(false);
    }
  };

  // Start countdown for page reload
  const startCountdown = () => {
    let seconds = 5;
    setCountdown(seconds);
    
    const timer = setInterval(() => {
      seconds--;
      setCountdown(seconds);
      
      if (seconds <= 0) {
        clearInterval(timer);
        window.location.reload();
      }
    }, 1000);
  };

  // Copy logs to clipboard
  const copyLogs = () => {
    const logText = deployLogs.join('\n');
    navigator.clipboard.writeText(logText);
    toast.success('Logs in Zwischenablage kopiert');
  };

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [deployLogs]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unbekannt';
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Don't render if not admin or no update available
  if (!isAdmin || !updateInfo?.update_available) {
    return null;
  }

  return (
    <>
      {/* Update Badge */}
      <button
        onClick={() => setShowModal(true)}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 border border-green-200 transition-colors group"
        title="Neue Version verfügbar - Klicken zum Installieren"
        data-testid="update-badge"
      >
        {/* Pulsating dot */}
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        </span>
        <span className="text-sm font-medium text-green-700 hidden sm:inline">Update</span>
      </button>

      {/* Update Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Download className="w-5 h-5 text-orange-500" />
              EasyWork Update verfügbar
            </DialogTitle>
          </DialogHeader>

          {/* Version Comparison */}
          {!isDeploying && !deployStatus && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Current Version */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 font-medium mb-1">Aktuell installiert</p>
                  <p className="font-mono text-lg font-bold text-gray-700">
                    {updateInfo?.current_version || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(updateInfo?.installed_at)}
                  </p>
                </div>
                
                {/* New Version */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-xs text-green-600 font-medium mb-1">Neue Version</p>
                  <p className="font-mono text-lg font-bold text-green-700">
                    {updateInfo?.new_version || 'N/A'}
                  </p>
                  <p className="text-xs text-green-500 mt-1">
                    {formatDate(updateInfo?.commit_date)}
                  </p>
                </div>
              </div>

              {/* Changelog */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 font-medium mb-2">Änderungen</p>
                <p className="text-sm text-gray-700 font-medium">
                  {updateInfo?.commit_message || 'Keine Beschreibung'}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  von {updateInfo?.commit_author || 'Unbekannt'}
                </p>
              </div>

              {/* Hint */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Das Update dauert ca. 60-90 Sekunden. Die Anwendung wird während des Updates kurzzeitig neu gestartet.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  data-testid="update-cancel-btn"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={startDeploy}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="update-deploy-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Jetzt updaten
                </Button>
              </div>
            </div>
          )}

          {/* Deploy in Progress / Terminal */}
          {(isDeploying || deployStatus) && (
            <div className="space-y-4">
              {/* Status indicator */}
              {isDeploying && (
                <div className="flex items-center gap-2 text-blue-600">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Update wird installiert...</span>
                </div>
              )}

              {/* Live Terminal */}
              <div 
                ref={terminalRef}
                className="bg-[#1a1a1a] rounded-lg p-4 h-[200px] overflow-y-auto font-mono text-xs"
                style={{ fontFamily: 'Courier New, monospace' }}
                data-testid="update-terminal"
              >
                {deployLogs.map((log, index) => (
                  <div key={index} className="text-[#00FF00] leading-relaxed">
                    {log}
                  </div>
                ))}
                {isDeploying && (
                  <div className="text-[#00FF00] animate-pulse">_</div>
                )}
              </div>

              {/* Success Banner */}
              {deployStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 font-medium">
                    <CheckCircle className="w-5 h-5" />
                    Update erfolgreich installiert!
                  </div>
                  {countdown !== null && (
                    <p className="text-sm text-green-600 mt-2">
                      Seite wird in {countdown} Sekunden neu geladen...
                    </p>
                  )}
                </div>
              )}

              {/* Failed Banner */}
              {deployStatus === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-medium">
                    <XCircle className="w-5 h-5" />
                    Update fehlgeschlagen
                  </div>
                  <p className="text-sm text-red-600 mt-1">
                    Bitte prüfe die Logs oder kontaktiere den Support.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyLogs}
                      data-testid="copy-logs-btn"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Log kopieren
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowModal(false)}
                    >
                      Schließen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UpdateChecker;
